import { useCallback, useEffect, useRef, useState } from 'react'
import type { SyntheticEvent } from 'react'
import { BottomNavigation, BottomNavigationAction, Paper, useTheme } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useShowBottomNav } from '@hooks/useShowBottomNav'
import { trackFeature } from '@utils/trackFeature'
import { bottomTabs, resolveBottomNavTab, resolveBottomTabPress, type BottomNavTabId } from './appNavConfig'
import { BOTTOM_NAV_HEIGHT } from './layoutConstants'
import { useShellOverlayMetrics } from './shellOverlayMetrics'
import MoreSheet from './MoreSheet'

const actionSx = {
  minWidth: 44,
  minHeight: 44,
  paddingTop: 0.5,
  paddingBottom: 0.5,
  '& .MuiBottomNavigationAction-label': {
    fontSize: '0.7rem',
  },
}

/**
 * Persistent bottom tab bar for mobile app shell (flag on, below md).
 */
export function AppBottomNavigation() {
  const show = useShowBottomNav()
  const theme = useTheme()
  const { keyboardInsetPx } = useShellOverlayMetrics()
  const location = useLocation()
  const navigate = useNavigate()
  const [moreOpen, setMoreOpen] = useState(false)
  const pendingTargetRef = useRef<string | null>(null)

  const activeTab = resolveBottomNavTab(location.pathname)

  useEffect(() => {
    pendingTargetRef.current = null
  }, [location.pathname])

  const handleChange = useCallback(
    (_event: SyntheticEvent, newValue: BottomNavTabId) => {
      const intent = resolveBottomTabPress(location.pathname, newValue, pendingTargetRef.current)

      if (intent.action === 'open-more') {
        trackFeature('nav_tab', { tab: 'more' })
        setMoreOpen(true)
        return
      }

      if (intent.action !== 'navigate') {
        return
      }

      pendingTargetRef.current = intent.target
      trackFeature('nav_tab', { tab: newValue })
      navigate(intent.target, { replace: intent.replace })
    },
    [location.pathname, navigate],
  )

  if (!show) {
    return null
  }

  return (
    <>
      <Paper
        component="nav"
        aria-label="Primary"
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: keyboardInsetPx > 0 ? `${keyboardInsetPx}px` : 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          borderTop: 1,
          borderColor: 'divider',
          pb: keyboardInsetPx > 0 ? 0 : 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <BottomNavigation
          value={activeTab}
          onChange={handleChange}
          showLabels
          sx={{ height: BOTTOM_NAV_HEIGHT, backgroundColor: 'background.paper' }}
        >
          {bottomTabs.map((tab) => (
            <BottomNavigationAction
              key={tab.id}
              label={tab.label}
              value={tab.id}
              icon={<tab.Icon />}
              sx={actionSx}
              aria-expanded={tab.id === 'more' ? moreOpen : undefined}
              aria-haspopup={tab.id === 'more' ? 'dialog' : undefined}
            />
          ))}
        </BottomNavigation>
      </Paper>
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  )
}

export default AppBottomNavigation
